// Shared mock sales fixtures - single source of truth for Sales list and SaleDetail
// Each fixture is tied to a transaction scenario for consistency

import {
  Sale,
  TransactionSource,
  TransactionStatus,
} from '../types/Transaction'
import { buildScenario } from '../../features/payments/utils/transactionScenarios'
import { computeTransactionViewModel } from '../../features/payments/utils/transactionViewModel'
export interface SaleFixture {
  id: string
  defaultScenarioId: string
  customerName: string
  customerEmail: string
  customerPhone?: string
  source: TransactionSource
  merchantName: string
  merchantId: string
  description: string
  paymentMethod: {
    type: 'card' | 'wallet' | 'bank_transfer' | 'cash'
    last4?: string
    brand?: string
    details?: string
  }
  invoiceId?: string
  settlementId?: string
}

// Define fixtures with customer/merchant/payment details
// Transaction data (id, amount, currency, date, status) comes from scenarios
const SALE_FIXTURES: SaleFixture[] = [
  {
    id: 'payment-with-reserve',
    defaultScenarioId: 'payment-with-reserve',
    customerName: 'Ahmed Saleem',
    customerEmail: 'ahmed.saleem@example.com',
    customerPhone: '+960 777 1234',
    source: 'payment_link',
    merchantName: 'Malé Mart',
    merchantId: 'MERCH-001',
    description: 'Simple payment with 20% reserve hold',
    paymentMethod: {
      type: 'card',
      last4: '4242',
      brand: 'Visa',
    },
    settlementId: 'SETTLE-2025-001',
  },
  {
    id: 'partial-refund',
    defaultScenarioId: 'partial-refund',
    customerName: 'Aishath Nazima',
    customerEmail: 'aishath.nazima@example.com',
    customerPhone: '+960 779 2345',
    source: 'qr_code',
    merchantName: 'Reef Restaurant',
    merchantId: 'MERCH-002',
    description: 'Payment with reserve, then partial refund',
    paymentMethod: {
      type: 'wallet',
      details: 'Apple Pay',
    },
    settlementId: 'SETTLE-2025-002',
  },
  {
    id: 'full-refund',
    defaultScenarioId: 'full-refund',
    customerName: 'Ibrahim Rasheed',
    customerEmail: 'ibrahim.rasheed@example.com',
    customerPhone: '+960 778 3456',
    source: 'invoice',
    merchantName: 'Atoll Tech Solutions',
    merchantId: 'MERCH-003',
    description: 'Payment fully refunded',
    paymentMethod: {
      type: 'card',
      last4: '5555',
      brand: 'Mastercard',
    },
    invoiceId: 'INV-2025-001',
    settlementId: 'SETTLE-2025-003',
  },
  {
    id: 'partial-dispute',
    defaultScenarioId: 'partial-dispute',
    customerName: 'Fathimath Shaira',
    customerEmail: 'fathimath.shaira@example.com',
    customerPhone: '+960 776 4567',
    source: 'tap_to_phone',
    merchantName: 'Lagoon Coffee',
    merchantId: 'MERCH-004',
    description: 'Payment with partial dispute (not yet resolved)',
    paymentMethod: {
      type: 'card',
      last4: '8888',
      brand: 'Visa',
    },
    settlementId: 'SETTLE-2025-004',
  },
  {
    id: 'complex-dispute-won',
    defaultScenarioId: 'complex-dispute-won',
    customerName: 'Mohamed Niyaz',
    customerEmail: 'mohamed.niyaz@example.com',
    customerPhone: '+960 775 5678',
    source: 'pos',
    merchantName: 'Island Electronics',
    merchantId: 'MERCH-005',
    description:
      'Complex: payment, reserve, partial refund, dispute received and won',
    paymentMethod: {
      type: 'card',
      last4: '3333',
      brand: 'Mastercard',
    },
    settlementId: 'SETTLE-2025-005',
  },
]

// Build complete Sale objects by combining fixtures with scenario data
export function buildMockSales(): Sale[] {
  return SALE_FIXTURES.map((fixture) => {
    const scenario = buildScenario(fixture.defaultScenarioId)
    const viewModel = computeTransactionViewModel(
      scenario.transaction,
      scenario.ledgerRows,
      'included',
    )

    // Map header state to Sale status
    let status: TransactionStatus = 'completed'
    if (viewModel.headerState === 'Refunded') {
      status = 'refunded'
    } else if (viewModel.headerState === 'Partially refunded') {
      status = 'partially_refunded'
    } else if (viewModel.headerState === 'Disputed') {
      status = 'completed' // Keep as completed for now
    } else if (viewModel.headerState === 'Dispute resolved') {
      status = 'completed'
    }
    return {
      id: fixture.id,
      transactionId: scenario.transaction.transactionId,
      amount: viewModel.headerAmount,
      currency: scenario.transaction.currency,
      status,
      source: fixture.source,
      date: scenario.transaction.date,
      customerName: fixture.customerName,
      customerEmail: fixture.customerEmail,
      customerPhone: fixture.customerPhone,
      paymentMethod: fixture.paymentMethod,
      merchantName: fixture.merchantName,
      merchantId: fixture.merchantId,
      description: fixture.description,
      fee: viewModel.summary.feesPostedDisplay,
      netAmount: viewModel.summary.netPosted,
      invoiceId: fixture.invoiceId,
      settlementId: fixture.settlementId,
      lifecycle: [
        {
          id: '1',
          timestamp: scenario.transaction.date,
          status: 'initiated',
          description: 'Transaction initiated',
        },
        {
          id: '2',
          timestamp: scenario.transaction.date,
          status: status,
          description: `Transaction ${status}`,
        },
      ],
    }
  })
}

// Get a single sale by ID
export function getMockSaleById(id: string): Sale | undefined {
  const sales = buildMockSales()
  return sales.find((sale) => sale.id === id)
}

// Get the fixture for a sale (for accessing defaultScenarioId)
export function getSaleFixture(id: string): SaleFixture | undefined {
  return SALE_FIXTURES.find((fixture) => fixture.id === id)
}
